<template>
    <div :class="classes">
        <div v-if="!loaded" class="bg-gray-300"/>
        <canvas ref="canvas" :class="classes"/>
        <slot/>
    </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import * as pdfjs from '@/dist/pdfjs/pdf';

const targetWidth = 450;
const targetHeight = 450;
const canvas = ref(null);
const canvasContext = ref(null);
const loaded = ref(false);

const props = defineProps({
    file: Object,
    path: String,
    classes: Array,
    alt: {
        type: String,
        default: ''
    },
});

const thumbnail = ref(null);
const fileObject = ref(null);
const isPdf = computed(() => {
    if (props.path) {
        return props.path.includes('.pdf');
    }

    return props.file?.type === 'application/pdf';
});

onMounted(async () => {
    pdfjs.GlobalWorkerOptions.workerSrc = '../../dist/pdfjs/pdf.worker.mjs';

    await setFile();
    canvasContext.value = canvas.value.getContext("2d");
    thumbnail.value = await generateThumbnail();
});

const setFile = async () => {
    if (props.file) {
        fileObject.value = props.file;
        return;
    }

    fileObject.value = await fetch(props.path).then(r => r.blob());
};

const generateThumbnail = async () => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
            try {
                if (isPdf.value) {
                    await generatePdfThumbnail(event, resolve, reject);
                    reader.readAsArrayBuffer(props.file)
                } else {
                    await generateImageThumbnail(event, resolve, reject);
                }
            } catch (e) {
                //
            }
        };

        reader.onerror = reject;
        reader.readAsDataURL(fileObject.value);
        loaded.value = true;
    });
};

const generatePdfThumbnail = async (event) => {
    const pdfData = event.target.result;

    const pdf = await pdfjsLib.getDocument({ data: getPdfBase64Data(pdfData) }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 0.5 });

    const aspectRatio = viewport.width / viewport.height;
    canvas.value.height = targetHeight;
    canvas.value.width = targetHeight * aspectRatio;

    const renderContext = {
        canvasContext: canvasContext.value,
        viewport: viewport,
    };
    await page.render(renderContext).promise;
};

const getPdfBase64Data = (data) => {
    if (typeof data !== 'string') {
        return;
    }

    const BASE64_MARKER = ';base64,';
    const base64Index = data.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
    const newUrl = data.substring(base64Index).replace(/[\n\r]/g,'');
    const raw = window.atob(newUrl);
    const rawLength = raw.length;

    const array = new Uint8Array(new ArrayBuffer(rawLength));
    for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i) & 0xff;
    }

    return array;
}

const generateImageThumbnail = async (event, resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        if (! canvas.value) {
            return;
        }

        // Scale image proportionally
        const aspectRatio = img.width / img.height;
        if (img.width > img.height) {
            canvas.value.width = targetWidth;
            canvas.value.height = targetWidth / aspectRatio;
        } else {
            canvas.value.height = targetHeight;
            canvas.value.width = targetHeight * aspectRatio;
        }

        // Draw the resized image to the canvas
        canvasContext.value.drawImage(img, 0, 0, canvas.value.width, canvas.value.height);

        // Convert canvas back to a data URL
        resolve(canvas.value.toDataURL(fileObject.value.type));
    };

    img.onerror = reject;
    img.src = event.target.result;
};
</script>
